import React, { useState, createContext, useContext } from 'react';
import { Workspace, workspaces, Form, initialForms, Template, initialTemplates, Submission, initialSubmissions, Subscription, initialSubscriptions, User, initialUsers, FormConfiguration, initialConfigs, Newsletter, initialNewsletters, Campaign, initialCampaigns, CampaignTemplate, initialCampaignTemplates, AuditEvent, initialAuditEvents, Tag, initialTags, AuditAction, AuditEntity, Role, initialRoles, Permission, Contact, initialContacts } from '../data/workspaceData';
interface WorkspaceContextType {
  currentWorkspace: Workspace;
  currentSubRegion: string | null;
  setCurrentWorkspace: (workspace: Workspace) => void;
  setCurrentSubRegion: (subRegion: string | null) => void;
  workspaces: Workspace[];
  forms: Form[];
  templates: Template[];
  submissions: Submission[];
  subscriptions: Subscription[];
  contacts: Contact[];
  confirmedContacts: Contact[];
  users: User[];
  allUsers: User[];
  formConfigs: FormConfiguration[];
  newsletters: Newsletter[];
  campaigns: Campaign[];
  campaignTemplates: CampaignTemplate[];
  auditEvents: AuditEvent[];
  tags: Tag[];
  roles: Role[];
  currentUser: User;
  addSubRegion: (subRegion: string) => void;
  removeSubRegion: (subRegion: string) => void;
  addContactCategory: (category: string) => void;
  removeContactCategory: (category: string) => void;
  createWorkspace: (workspace: Omit<Workspace, 'id' | 'subRegions' | 'contactCategories'>) => void;
  createUser: (user: Omit<User, 'id' | 'status'>) => void;
  updateUserStatus: (userId: number, status: User['status']) => void;
  updateUserRole: (userId: number, roleId: number) => void;
  deleteUser: (userId: number) => void;
  saveFormConfig: (config: Omit<FormConfiguration, 'id' | 'createdAt'>) => void;
  deleteFormConfig: (id: number) => void;
  createNewsletter: (newsletter: Omit<Newsletter, 'id' | 'createdAt' | 'subscriberCount'>) => void;
  updateNewsletterIntake: (id: number, intakeStatus: Newsletter['intakeStatus'], message?: string) => void;
  createCampaign: (campaign: Omit<Campaign, 'id' | 'createdAt' | 'status' | 'sentCount' | 'failedCount' | 'recipientCount'>) => void;
  createCampaignTemplate: (template: Omit<CampaignTemplate, 'id' | 'createdAt'>) => void;
  createTag: (tag: Omit<Tag, 'id' | 'createdAt'>) => void;
  deleteTag: (id: number) => void;
  createContact: (contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateContact: (id: number, updates: Partial<Contact>) => void;
  deleteContact: (id: number) => void;
  getContactsByCategory: (category: string) => Contact[];
  getConfirmedContactsByList: (listId: number) => Contact[];
  getSubmissionsByTag: (tagName: string) => Submission[];
  getSubmissionsByMailingList: (listId: number) => Submission[];
  getSubscriptionsByMailingList: (listId: number) => Subscription[];
  getUnconfiguredForms: () => Form[];
  getUnassignedForms: () => Form[];
  duplicateForm: (formId: number) => void;
  duplicateNewsletter: (newsletterId: number) => void;
  duplicateTemplate: (templateId: number) => void;
  logAuditEvent: (action: AuditAction, entityType: AuditEntity, entityId: number, entityName: string, changes?: {
    field: string;
    oldValue: string | null;
    newValue: string | null;
  }[], metadata?: Record<string, any>) => void;
  createRole: (role: Omit<Role, 'id' | 'createdAt' | 'isSystem'>) => void;
  updateRole: (id: number, updates: Partial<Omit<Role, 'id' | 'createdAt' | 'isSystem'>>) => void;
  deleteRole: (id: number) => void;
  getRoleById: (id: number) => Role | undefined;
  hasPermission: (permission: Permission) => boolean;
  getUserPermissions: (userId?: number) => Permission[];
}
const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);
export function WorkspaceProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace>(workspaces[0]);
  const [currentSubRegion, setCurrentSubRegion] = useState<string | null>(null);
  const [allWorkspaces, setAllWorkspaces] = useState<Workspace[]>(workspaces);
  const [allUsersState, setAllUsersState] = useState<User[]>(initialUsers);
  const [allForms, setAllForms] = useState<Form[]>(initialForms);
  const [allTemplates, setAllTemplates] = useState<Template[]>(initialTemplates);
  const [allSubmissions, setAllSubmissions] = useState<Submission[]>(initialSubmissions);
  const [allSubscriptions, setAllSubscriptions] = useState<Subscription[]>(initialSubscriptions);
  const [allContacts, setAllContacts] = useState<Contact[]>(initialContacts);
  const [allConfigs, setAllConfigs] = useState<FormConfiguration[]>(initialConfigs);
  const [allNewsletters, setAllNewsletters] = useState<Newsletter[]>(initialNewsletters);
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>(initialCampaigns);
  const [allCampaignTemplates, setAllCampaignTemplates] = useState<CampaignTemplate[]>(initialCampaignTemplates);
  const [allAuditEvents, setAllAuditEvents] = useState<AuditEvent[]>(initialAuditEvents);
  const [allTags, setAllTags] = useState<Tag[]>(initialTags);
  const [allRoles, setAllRoles] = useState<Role[]>(initialRoles);
  const currentUser = allUsersState.find(u => u.role === 'Super Admin') || initialUsers[0];
  const filterByScope = <T extends {
    workspaceId: string;
    subRegion?: string;
  },>(items: T[]) => {
    return items.filter(item => {
      if (item.workspaceId !== currentWorkspace.id) return false;
      if (currentSubRegion) {
        return !item.subRegion || item.subRegion === currentSubRegion;
      }
      return true;
    });
  };
  const forms = filterByScope(allForms);
  const templates = filterByScope(allTemplates);
  const submissions = filterByScope(allSubmissions);
  const subscriptions = filterByScope(allSubscriptions);
  const contacts = filterByScope(allContacts);
  const confirmedContacts = contacts.filter(c => c.optInStatus === 'confirmed');
  const formConfigs = filterByScope(allConfigs);
  const newsletters = filterByScope(allNewsletters);
  const campaigns = filterByScope(allCampaigns);
  const campaignTemplates = filterByScope(allCampaignTemplates);
  const auditEvents = filterByScope(allAuditEvents);
  const tags = filterByScope(allTags);
  const users = allUsersState.filter(u => u.workspaceId === currentWorkspace.id);
  const getRoleById = (id: number) => allRoles.find(r => r.id === id);
  const getUserPermissions = (userId?: number): Permission[] => {
    const user = userId ? allUsersState.find(u => u.id === userId) : currentUser;
    if (!user) return [];
    const role = getRoleById(user.roleId);
    return role?.permissions || [];
  };
  const hasPermission = (permission: Permission): boolean => {
    const permissions = getUserPermissions();
    return permissions.includes(permission);
  };
  const logAuditEvent = (action: AuditAction, entityType: AuditEntity, entityId: number, entityName: string, changes?: {
    field: string;
    oldValue: string | null;
    newValue: string | null;
  }[], metadata?: Record<string, any>) => {
    const newEvent: AuditEvent = {
      id: Math.max(...allAuditEvents.map(e => e.id), 0) + 1,
      workspaceId: currentWorkspace.id,
      subRegion: currentSubRegion || undefined,
      userId: currentUser.id,
      userName: currentUser.name,
      userEmail: currentUser.email,
      action,
      entityType,
      entityId,
      entityName,
      changes,
      metadata,
      timestamp: new Date().toLocaleString(),
      ipAddress: '192.168.1.1'
    };
    setAllAuditEvents([newEvent, ...allAuditEvents]);
  };
  // Contact management
  const createContact = (contactData: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newContact: Contact = {
      ...contactData,
      id: Math.max(...allContacts.map(c => c.id), 0) + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setAllContacts([...allContacts, newContact]);
    logAuditEvent('created', 'contact', newContact.id, newContact.email);
  };
  const updateContact = (id: number, updates: Partial<Contact>) => {
    const contact = allContacts.find(c => c.id === id);
    if (contact) {
      const changes: {
        field: string;
        oldValue: string | null;
        newValue: string | null;
      }[] = [];
      if (updates.category !== undefined && updates.category !== contact.category) {
        changes.push({
          field: 'category',
          oldValue: contact.category || null,
          newValue: updates.category || null
        });
      }
      if (updates.optInStatus !== undefined && updates.optInStatus !== contact.optInStatus) {
        changes.push({
          field: 'optInStatus',
          oldValue: contact.optInStatus,
          newValue: updates.optInStatus
        });
      }
      setAllContacts(allContacts.map(c => c.id === id ? {
        ...c,
        ...updates,
        updatedAt: new Date().toISOString()
      } : c));
      if (changes.length > 0) {
        logAuditEvent('updated', 'contact', id, contact.email, changes);
      }
    }
  };
  const deleteContact = (id: number) => {
    const contact = allContacts.find(c => c.id === id);
    if (contact) {
      setAllContacts(allContacts.filter(c => c.id !== id));
      logAuditEvent('deleted', 'contact', id, contact.email);
    }
  };
  const getContactsByCategory = (category: string) => {
    return confirmedContacts.filter(c => c.category === category);
  };
  const getConfirmedContactsByList = (listId: number) => {
    return confirmedContacts.filter(c => c.subscribedLists.includes(listId));
  };
  const getSubmissionsByTag = (tagName: string) => submissions.filter(s => s.tags?.includes(tagName));
  const getSubmissionsByMailingList = (listId: number) => submissions.filter(s => s.mailingListIds?.includes(listId));
  const getSubscriptionsByMailingList = (listId: number) => subscriptions.filter(s => s.listId === listId);
  const getUnconfiguredForms = () => {
    const configuredFormIds = formConfigs.map(c => c.formId);
    return forms.filter(f => !configuredFormIds.includes(f.id));
  };
  const getUnassignedForms = () => {
    const assignedFormIds = newsletters.filter(n => n.formId).map(n => n.formId);
    return forms.filter(f => !assignedFormIds.includes(f.id));
  };
  const addSubRegion = (subRegion: string) => {
    const updatedWorkspaces = allWorkspaces.map(ws => {
      if (ws.id === currentWorkspace.id) {
        return {
          ...ws,
          subRegions: [...ws.subRegions, subRegion]
        };
      }
      return ws;
    });
    setAllWorkspaces(updatedWorkspaces);
    const updatedCurrent = updatedWorkspaces.find(ws => ws.id === currentWorkspace.id);
    if (updatedCurrent) setCurrentWorkspace(updatedCurrent);
    logAuditEvent('created', 'workspace', 0, subRegion, undefined, {
      type: 'subregion'
    });
  };
  const removeSubRegion = (subRegion: string) => {
    const updatedWorkspaces = allWorkspaces.map(ws => {
      if (ws.id === currentWorkspace.id) {
        return {
          ...ws,
          subRegions: ws.subRegions.filter(sr => sr !== subRegion)
        };
      }
      return ws;
    });
    setAllWorkspaces(updatedWorkspaces);
    const updatedCurrent = updatedWorkspaces.find(ws => ws.id === currentWorkspace.id);
    if (updatedCurrent) setCurrentWorkspace(updatedCurrent);
    logAuditEvent('deleted', 'workspace', 0, subRegion, undefined, {
      type: 'subregion'
    });
  };
  const addContactCategory = (category: string) => {
    const updatedWorkspaces = allWorkspaces.map(ws => {
      if (ws.id === currentWorkspace.id) {
        return {
          ...ws,
          contactCategories: [...(ws.contactCategories || []), category]
        };
      }
      return ws;
    });
    setAllWorkspaces(updatedWorkspaces);
    const updatedCurrent = updatedWorkspaces.find(ws => ws.id === currentWorkspace.id);
    if (updatedCurrent) setCurrentWorkspace(updatedCurrent);
    logAuditEvent('created', 'workspace', 0, category, undefined, {
      type: 'contact_category'
    });
  };
  const removeContactCategory = (category: string) => {
    const updatedWorkspaces = allWorkspaces.map(ws => {
      if (ws.id === currentWorkspace.id) {
        return {
          ...ws,
          contactCategories: (ws.contactCategories || []).filter(c => c !== category)
        };
      }
      return ws;
    });
    setAllWorkspaces(updatedWorkspaces);
    const updatedCurrent = updatedWorkspaces.find(ws => ws.id === currentWorkspace.id);
    if (updatedCurrent) setCurrentWorkspace(updatedCurrent);
    logAuditEvent('deleted', 'workspace', 0, category, undefined, {
      type: 'contact_category'
    });
  };
  const createWorkspace = (workspaceData: Omit<Workspace, 'id' | 'subRegions' | 'contactCategories'>) => {
    const newWorkspace: Workspace = {
      ...workspaceData,
      id: workspaceData.name.toLowerCase().replace(/\s+/g, '-'),
      subRegions: [],
      contactCategories: []
    };
    setAllWorkspaces([...allWorkspaces, newWorkspace]);
    logAuditEvent('created', 'workspace', 0, newWorkspace.name);
  };
  const createUser = (userData: Omit<User, 'id' | 'status'>) => {
    const newUser: User = {
      ...userData,
      id: Math.max(...allUsersState.map(u => u.id)) + 1,
      status: 'active'
    };
    setAllUsersState([...allUsersState, newUser]);
    logAuditEvent('created', 'user', newUser.id, newUser.name);
  };
  const updateUserStatus = (userId: number, status: User['status']) => {
    const user = allUsersState.find(u => u.id === userId);
    if (user) {
      const oldStatus = user.status;
      setAllUsersState(allUsersState.map(u => u.id === userId ? {
        ...u,
        status
      } : u));
      logAuditEvent('status_changed', 'user', userId, user.name, [{
        field: 'status',
        oldValue: oldStatus,
        newValue: status
      }]);
    }
  };
  const updateUserRole = (userId: number, roleId: number) => {
    const user = allUsersState.find(u => u.id === userId);
    const newRole = getRoleById(roleId);
    if (user && newRole) {
      const oldRole = user.role;
      setAllUsersState(allUsersState.map(u => u.id === userId ? {
        ...u,
        roleId,
        role: newRole.name as any
      } : u));
      logAuditEvent('updated', 'user', userId, user.name, [{
        field: 'role',
        oldValue: oldRole,
        newValue: newRole.name
      }]);
    }
  };
  const deleteUser = (userId: number) => {
    const user = allUsersState.find(u => u.id === userId);
    if (user) {
      setAllUsersState(allUsersState.filter(u => u.id !== userId));
      logAuditEvent('deleted', 'user', userId, user.name);
    }
  };
  const saveFormConfig = (configData: Omit<FormConfiguration, 'id' | 'createdAt'>) => {
    const form = forms.find(f => f.id === configData.formId);
    const newConfig: FormConfiguration = {
      ...configData,
      id: Math.max(...allConfigs.map(c => c.id), 0) + 1,
      createdAt: new Date().toISOString()
    };
    setAllConfigs([...allConfigs, newConfig]);
    logAuditEvent('created', 'form_config', newConfig.id, form?.name || 'Unknown Form');
  };
  const deleteFormConfig = (id: number) => {
    const config = allConfigs.find(c => c.id === id);
    const form = forms.find(f => f.id === config?.formId);
    setAllConfigs(allConfigs.filter(c => c.id !== id));
    logAuditEvent('deleted', 'form_config', id, form?.name || 'Unknown Form');
  };
  const createNewsletter = (newsletterData: Omit<Newsletter, 'id' | 'createdAt' | 'subscriberCount'>) => {
    const newNewsletter: Newsletter = {
      ...newsletterData,
      id: Math.max(...allNewsletters.map(n => n.id), 0) + 1,
      subscriberCount: 0,
      createdAt: new Date().toISOString()
    };
    setAllNewsletters([...allNewsletters, newNewsletter]);
    logAuditEvent('created', 'newsletter', newNewsletter.id, newNewsletter.name);
  };
  const updateNewsletterIntake = (id: number, intakeStatus: Newsletter['intakeStatus'], message?: string) => {
    const newsletter = allNewsletters.find(n => n.id === id);
    if (newsletter) {
      const oldStatus = newsletter.intakeStatus;
      setAllNewsletters(allNewsletters.map(n => {
        if (n.id === id) {
          return {
            ...n,
            intakeStatus,
            intakePausedMessage: intakeStatus === 'paused' ? message : n.intakePausedMessage,
            intakeClosedMessage: intakeStatus === 'closed' ? message : n.intakeClosedMessage
          };
        }
        return n;
      }));
      logAuditEvent('status_changed', 'newsletter', id, newsletter.name, [{
        field: 'intakeStatus',
        oldValue: oldStatus,
        newValue: intakeStatus
      }]);
    }
  };
  const createCampaign = (campaignData: Omit<Campaign, 'id' | 'createdAt' | 'status' | 'sentCount' | 'failedCount' | 'recipientCount'>) => {
    const newCampaign: Campaign = {
      ...campaignData,
      id: Math.max(...allCampaigns.map(c => c.id), 0) + 1,
      status: (campaignData as any).status || 'draft',
      recipientCount: (campaignData as any).recipientCount || 0,
      sentCount: (campaignData as any).sentCount || 0,
      failedCount: (campaignData as any).failedCount || 0,
      createdAt: new Date().toISOString()
    };
    setAllCampaigns([...allCampaigns, newCampaign]);
    logAuditEvent('created', 'campaign', newCampaign.id, newCampaign.name);
  };
  const createCampaignTemplate = (templateData: Omit<CampaignTemplate, 'id' | 'createdAt'>) => {
    const newTemplate: CampaignTemplate = {
      ...templateData,
      id: Math.max(...allCampaignTemplates.map(t => t.id), 0) + 1,
      createdAt: new Date().toISOString()
    };
    setAllCampaignTemplates([...allCampaignTemplates, newTemplate]);
  };
  const createTag = (tagData: Omit<Tag, 'id' | 'createdAt'>) => {
    const newTag: Tag = {
      ...tagData,
      id: Math.max(...allTags.map(t => t.id), 0) + 1,
      createdAt: new Date().toISOString()
    };
    setAllTags([...allTags, newTag]);
    logAuditEvent('created', 'tag', newTag.id, newTag.name);
  };
  const deleteTag = (id: number) => {
    const tag = allTags.find(t => t.id === id);
    if (tag) {
      setAllTags(allTags.filter(t => t.id !== id));
      logAuditEvent('deleted', 'tag', id, tag.name);
    }
  };
  const duplicateForm = (formId: number) => {
    const form = allForms.find(f => f.id === formId);
    if (form) {
      const newForm: Form = {
        ...form,
        id: Math.max(...allForms.map(f => f.id), 0) + 1,
        name: `${form.name} (Copy)`,
        slug: `${form.slug}-copy`,
        updated: 'Just now'
      };
      setAllForms([...allForms, newForm]);
      logAuditEvent('duplicated', 'form', formId, form.name, undefined, {
        newId: newForm.id,
        newName: newForm.name
      });
    }
  };
  const duplicateNewsletter = (newsletterId: number) => {
    const newsletter = allNewsletters.find(n => n.id === newsletterId);
    if (newsletter) {
      const newNewsletter: Newsletter = {
        ...newsletter,
        id: Math.max(...allNewsletters.map(n => n.id), 0) + 1,
        name: `${newsletter.name} (Copy)`,
        formId: undefined,
        intakeStatus: 'open',
        subscriberCount: 0,
        createdAt: new Date().toISOString()
      };
      setAllNewsletters([...allNewsletters, newNewsletter]);
      logAuditEvent('duplicated', 'newsletter', newsletterId, newsletter.name, undefined, {
        newId: newNewsletter.id,
        newName: newNewsletter.name
      });
    }
  };
  const duplicateTemplate = (templateId: number) => {
    const template = allTemplates.find(t => t.id === templateId);
    if (template) {
      const newTemplate: Template = {
        ...template,
        id: Math.max(...allTemplates.map(t => t.id), 0) + 1,
        name: `${template.name} (Copy)`,
        status: 'draft'
      };
      setAllTemplates([...allTemplates, newTemplate]);
      logAuditEvent('duplicated', 'template', templateId, template.name, undefined, {
        newId: newTemplate.id,
        newName: newTemplate.name
      });
    }
  };
  const createRole = (roleData: Omit<Role, 'id' | 'createdAt' | 'isSystem'>) => {
    const newRole: Role = {
      ...roleData,
      id: Math.max(...allRoles.map(r => r.id), 0) + 1,
      isSystem: false,
      createdAt: new Date().toISOString()
    };
    setAllRoles([...allRoles, newRole]);
    logAuditEvent('created', 'role', newRole.id, newRole.name);
  };
  const updateRole = (id: number, updates: Partial<Omit<Role, 'id' | 'createdAt' | 'isSystem'>>) => {
    const role = allRoles.find(r => r.id === id);
    if (role) {
      setAllRoles(allRoles.map(r => r.id === id ? {
        ...r,
        ...updates
      } : r));
      logAuditEvent('updated', 'role', id, role.name, updates.permissions ? [{
        field: 'permissions',
        oldValue: `${role.permissions.length} permissions`,
        newValue: `${updates.permissions.length} permissions`
      }] : undefined);
    }
  };
  const deleteRole = (id: number) => {
    const role = allRoles.find(r => r.id === id);
    if (role && !role.isSystem) {
      setAllRoles(allRoles.filter(r => r.id !== id));
      logAuditEvent('deleted', 'role', id, role.name);
    }
  };
  return <WorkspaceContext.Provider value={{
    currentWorkspace,
    currentSubRegion,
    setCurrentWorkspace,
    setCurrentSubRegion,
    workspaces: allWorkspaces,
    forms,
    templates,
    submissions,
    subscriptions,
    contacts,
    confirmedContacts,
    users,
    allUsers: allUsersState,
    formConfigs,
    newsletters,
    campaigns,
    campaignTemplates,
    auditEvents,
    tags,
    roles: allRoles,
    currentUser,
    addSubRegion,
    removeSubRegion,
    addContactCategory,
    removeContactCategory,
    createWorkspace,
    createUser,
    updateUserStatus,
    updateUserRole,
    deleteUser,
    saveFormConfig,
    deleteFormConfig,
    createNewsletter,
    updateNewsletterIntake,
    createCampaign,
    createCampaignTemplate,
    createTag,
    deleteTag,
    createContact,
    updateContact,
    deleteContact,
    getContactsByCategory,
    getConfirmedContactsByList,
    getSubmissionsByTag,
    getSubmissionsByMailingList,
    getSubscriptionsByMailingList,
    getUnconfiguredForms,
    getUnassignedForms,
    duplicateForm,
    duplicateNewsletter,
    duplicateTemplate,
    logAuditEvent,
    createRole,
    updateRole,
    deleteRole,
    getRoleById,
    hasPermission,
    getUserPermissions
  }} data-id="element-395">
      {children}
    </WorkspaceContext.Provider>;
}
export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}